import { useToast } from '@chakra-ui/react';
import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import type { UploadFileResult } from '@revelio/data-access';

import type { ChartExportInfo } from './chart-export-types';
import {
  type ChartSheetData,
  downloadMultiSheetData,
} from './data-download/download-plot-data';
import type { PdfCoverParams } from './data-download/pdf-export/pdf-exporter';
import {
  exportElementToPdfWithResult,
  generatePdfBlob,
} from './data-download/pdf-export/pdf-exporter';
import {
  exportMultipleChartsToPpt,
  showExportToast,
  showUploadToast,
} from './data-download/ppt-export/ppt-exporter';
import {
  exportReportToPpt,
  type ReportExportParams,
} from './data-download/ppt-export/ppt-report-exporter';
import { dataDownloadRegistry } from './data-download/registry';
import { uploadFileToStorage } from './data-upload/upload-file';

const mergeChartExportInfo = (
  prior: ChartExportInfo,
  incoming: Partial<ChartExportInfo>
): ChartExportInfo => {
  const result: ChartExportInfo = { ...prior };
  (Object.keys(incoming) as (keyof ChartExportInfo)[]).forEach((key) => {
    const value = incoming[key];
    if (value !== undefined) {
      Object.assign(result, { [key]: value });
    }
  });
  return result;
};

export type ReportExportMetadata = Omit<ReportExportParams, 'charts'>;

export type { ChartExportInfo } from './chart-export-types';

interface PageExportContextType {
  registerChart: (id: string, info: ChartExportInfo) => void;
  unregisterChart: (id: string) => void;
  updateChartExportInfo: (
    id: string,
    partial: Partial<ChartExportInfo>
  ) => void;
  updateChartLoadingState: (id: string, isLoading: boolean) => void;
  setPptExportResolver: (fn: (() => ChartExportInfo[]) | null) => void;
  setPptAuxiliaryBlockingCount: (count: number) => void;
  setPageHasTextOrAiCharts: (hasTextOrAi: boolean) => void;
  setReportExportMetadata: (metadata: ReportExportMetadata | null) => void;
  getChartRegistrySnapshot: () => Map<string, ChartExportInfo>;
  pdfExportRef: MutableRefObject<HTMLDivElement | null>;
  setDefaultExportFileName: (fileName: string) => void;
  exportAllCharts: () => Promise<void>;
  exportAllChartsToPdf: (options?: { cover?: PdfCoverParams }) => Promise<void>;
  exportAllChartsData: (fileName: string) => void;
  previewAllCharts: () => Promise<UploadFileResult | null>;
  previewAllChartsPdf: (options?: {
    cover?: PdfCoverParams;
  }) => Promise<string | null>;
  isExporting: boolean;
  isPageDataReady: boolean;
}

export const PageExportContext = createContext<
  PageExportContextType | undefined
>(undefined);

export const PageExportProvider = ({ children }: { children: ReactNode }) => {
  const chartRegistry = useRef<Map<string, ChartExportInfo>>(new Map());
  const loadingStateRef = useRef<Map<string, boolean>>(new Map());
  const [loadingCount, setLoadingCount] = useState(0);
  const [unregisteredPendingCount, setUnregisteredPendingCount] = useState(0);
  const [hasRegisteredCharts, setHasRegisteredCharts] = useState(false);
  const [pageHasTextOrAiCharts, setPageHasTextOrAiCharts] = useState(false);
  const [pptAuxiliaryBlockingCount, setPptAuxiliaryBlockingCount] = useState(0);
  const pptExportResolverRef = useRef<(() => ChartExportInfo[]) | null>(null);
  const reportMetadataRef = useRef<ReportExportMetadata | null>(null);
  const pdfExportElementRef = useRef<HTMLDivElement | null>(null);
  const defaultExportFileNameRef = useRef<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const hasExportSources = hasRegisteredCharts || pageHasTextOrAiCharts;

  const isPageDataReady =
    hasExportSources &&
    loadingCount === 0 &&
    unregisteredPendingCount === 0 &&
    pptAuxiliaryBlockingCount === 0;

  const recomputeReadinessCounts = () => {
    const nextLoadingCount = Array.from(chartRegistry.current.keys()).reduce(
      (count, id) => {
        if (loadingStateRef.current.get(id) === true) {
          return count + 1;
        }
        return count;
      },
      0
    );
    setLoadingCount(nextLoadingCount);

    const nextUnregisteredPendingCount = Array.from(
      loadingStateRef.current.entries()
    ).reduce((count, [id, isLoading]) => {
      if (isLoading && !chartRegistry.current.has(id)) {
        return count + 1;
      }
      return count;
    }, 0);
    setUnregisteredPendingCount(nextUnregisteredPendingCount);
  };

  const registerChart = (id: string, info: ChartExportInfo) => {
    const prior = chartRegistry.current.get(id);
    chartRegistry.current.set(
      id,
      prior === undefined ? info : mergeChartExportInfo(prior, info)
    );
    setHasRegisteredCharts(true);
    const knownLoading = loadingStateRef.current.get(id);
    loadingStateRef.current.set(id, knownLoading ?? false);
    recomputeReadinessCounts();
  };

  const unregisterChart = (id: string) => {
    chartRegistry.current.delete(id);
    loadingStateRef.current.delete(id);
    recomputeReadinessCounts();
  };

  const updateChartExportInfo = useCallback(
    (id: string, partial: Partial<ChartExportInfo>) => {
      const existing = chartRegistry.current.get(id);
      if (!existing) return;
      chartRegistry.current.set(id, mergeChartExportInfo(existing, partial));
    },
    []
  );

  const updateChartLoadingState = (id: string, isLoading: boolean) => {
    loadingStateRef.current.set(id, isLoading);
    recomputeReadinessCounts();
  };

  const setPptExportResolver = useCallback(
    (fn: (() => ChartExportInfo[]) | null) => {
      pptExportResolverRef.current = fn;
    },
    []
  );

  const setReportExportMetadata = useCallback(
    (metadata: ReportExportMetadata | null) => {
      reportMetadataRef.current = metadata;
    },
    []
  );

  const setDefaultExportFileName = (fileName: string) => {
    defaultExportFileNameRef.current = fileName;
  };

  const getChartRegistrySnapshot = useCallback(
    () => new Map(chartRegistry.current),
    []
  );

  const resolveChartsForPpt = (): ChartExportInfo[] => {
    const resolver = pptExportResolverRef.current;
    if (resolver) {
      return resolver();
    }
    return Array.from(chartRegistry.current.values());
  };

  const exportAllCharts = async () => {
    if (isExporting) return;
    const charts = resolveChartsForPpt();
    const reportMetadata = reportMetadataRef.current;
    if (charts.length === 0 && !reportMetadata) {
      console.warn('No content registered for export.');
      return;
    }

    try {
      const exportPromise = reportMetadata
        ? exportReportToPpt({ ...reportMetadata, charts }).getFile()
        : exportMultipleChartsToPpt({ charts }).getFile();
      showExportToast(toast, exportPromise);
    } catch (e) {
      console.error('Error exporting dashboard:', e);
    }
  };

  const exportAllChartsToPdf = async (options?: { cover?: PdfCoverParams }) => {
    if (isExporting) return;

    const element = pdfExportElementRef.current;
    if (!element) {
      console.warn('No PDF export element registered.');
      return;
    }

    const charts = Array.from(chartRegistry.current.values());
    const lastDefaultFileName = charts[charts.length - 1]?.defaultFileName;
    const fileNameBase =
      lastDefaultFileName?.split(' - ').slice(0, -1).join(' - ') ||
      defaultExportFileNameRef.current ||
      'dashboard-export';

    setIsExporting(true);
    try {
      const exportPromise = exportElementToPdfWithResult({
        element,
        fileNameBase,
        cover: options?.cover,
      }).getFile();
      showExportToast(toast, exportPromise);
      await exportPromise;
    } catch (e) {
      console.error('Error exporting dashboard PDF:', e);
    } finally {
      setIsExporting(false);
    }
  };

  const previewAllCharts = async (): Promise<UploadFileResult | null> => {
    if (isExporting) return null;
    const charts = resolveChartsForPpt();
    const reportMetadata = reportMetadataRef.current;
    if (charts.length === 0 && !reportMetadata) {
      console.warn('No content registered for export.');
      return null;
    }

    try {
      const uploadPromise = (async () => {
        const result = reportMetadata
          ? await exportReportToPpt({ ...reportMetadata, charts }).getBlob()
          : await exportMultipleChartsToPpt({ charts }).getBlob();
        if (!result) {
          throw new Error('Failed to prepare PPT for preview.');
        }
        return await uploadFileToStorage(result);
      })();

      showUploadToast(toast, uploadPromise);
      return await uploadPromise;
    } catch (e) {
      console.error('Error exporting dashboard for preview:', e);
      return null;
    }
  };

  const previewAllChartsPdf = async (options?: {
    cover?: PdfCoverParams;
  }): Promise<string | null> => {
    if (isExporting) return null;

    const element = pdfExportElementRef.current;
    if (!element) {
      console.warn('No PDF export element registered.');
      return null;
    }

    const charts = Array.from(chartRegistry.current.values());
    const lastDefaultFileName = charts[charts.length - 1]?.defaultFileName;
    const fileNameBase =
      lastDefaultFileName?.split(' - ').slice(0, -1).join(' - ') ||
      defaultExportFileNameRef.current ||
      'dashboard-export';

    setIsExporting(true);
    try {
      const result = await generatePdfBlob({
        element,
        fileNameBase,
        cover: options?.cover,
      });

      if (!result) throw new Error('Failed to generate PDF');

      const blobUrl = URL.createObjectURL(result.blob);

      return blobUrl;
    } catch (e) {
      console.error('Error exporting dashboard PDF for preview:', e);
      return null;
    } finally {
      setIsExporting(false);
    }
  };

  const exportAllChartsData = (fileName: string) => {
    const charts = Array.from(chartRegistry.current.values());
    if (charts.length === 0) {
      console.warn('No content registered for data export.');
      return;
    }

    setTimeout(async () => {
      try {
        const sheets: ChartSheetData[] = [];

        for (const chart of charts) {
          try {
            const { data, dataType, title } = chart;

            if (!data || !dataType) {
              console.warn(`No data available for chart: ${title}`);
              continue;
            }

            const converter = dataDownloadRegistry[dataType];
            if (!converter) {
              console.warn(`No converter found for data type: ${dataType}`);
              continue;
            }

            const columnLabels = chart.columnLabels || {
              primary: 'Dimension',
              secondary: 'Secondary Dimension',
              metric: 'Value',
            };

            const { sheetData, skipHeader = false } = converter(
              data,
              columnLabels.primary,
              columnLabels.secondary,
              columnLabels.metric
            );

            if (sheetData.length === 0) {
              console.warn(`No data available for chart: ${title}`);
              continue;
            }

            sheets.push({
              title,
              data: sheetData,
              options: { skipHeader },
            });
          } catch (error) {
            console.error(`Error processing chart ${chart.title}:`, error);
          }
        }

        if (sheets.length > 0) {
          downloadMultiSheetData(sheets, fileName);
        } else {
          console.warn('No valid data to export');
        }
      } catch (e) {
        console.error('Error exporting dashboard data:', e);
      }
    }, 0);
  };

  return (
    <PageExportContext.Provider
      value={{
        registerChart,
        unregisterChart,
        updateChartExportInfo,
        updateChartLoadingState,
        setPptExportResolver,
        setPptAuxiliaryBlockingCount,
        setPageHasTextOrAiCharts,
        setReportExportMetadata,
        getChartRegistrySnapshot,
        pdfExportRef: pdfExportElementRef,
        setDefaultExportFileName,
        exportAllCharts,
        exportAllChartsToPdf,
        exportAllChartsData,
        previewAllCharts,
        previewAllChartsPdf,
        isExporting,
        isPageDataReady,
      }}
    >
      {children}
    </PageExportContext.Provider>
  );
};

export const usePageExport = (): PageExportContextType => {
  const ctx = useContext(PageExportContext);
  if (!ctx) {
    throw new Error('usePageExport must be used within a PageExportProvider');
  }
  return ctx;
};
